import { Button, Checkbox, Modal, Tree, useTree } from '@mantine/core';
import { useState, useCallback } from 'react';

import { DirectoryLeaf } from 'src/Components/Modals/DirectorySelectionModal/DirectoryLeaf';

import { CustomTreeNodeData } from './CustomTreeType';
import styles from './DirectorySelectionModal.module.scss';
import { Props, SelectedItem } from './DirectorySelectionModal.types';

const updateTreeNodeChildren = (
  currentNodes: CustomTreeNodeData[],
  targetPath: string,
  newChildrenApiData: CustomTreeNodeData[],
  shouldSelectFile?: boolean
): CustomTreeNodeData[] => {
  return currentNodes.map(node => {
    if (node.value === targetPath) {
      return {
        ...node,
        children: newChildrenApiData
          .filter(node => node.isFolder || (shouldSelectFile && !node.isFolder))
          .map((childApiItem: CustomTreeNodeData) => {
            return {
              id: childApiItem.id,
              value: childApiItem.value,
              label: childApiItem.label,
              children: childApiItem.isFolder ? [] : undefined,
              isFolder: childApiItem.isFolder,
              lastModified: childApiItem.lastModified,
              uploader: childApiItem.uploader,
            };
          }),
      };
    } else if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: updateTreeNodeChildren(
          node.children,
          targetPath,
          newChildrenApiData,
          shouldSelectFile
        ),
      };
    }
    return node;
  });
};

const DirectorySelectionModal = ({
  open,
  onClose,
  title,
  subtitle,
  buttonText,
  onValidate,
  loading,
  shouldSelectFile,
  multiple,
}: Props) => {
  const [checkedFiles, setCheckedFiles] = useState<Set<string>>(new Set());
  const [treeData, setTreeData] = useState<CustomTreeNodeData[]>([
    {
      id: '',
      value: '/',
      label: '/',
      children: [],
      isFolder: true,
    },
  ]);

  const handleChildrenLoaded = useCallback(
    (parentPath: string, childrenApiData: CustomTreeNodeData[]) => {
      setTreeData(prevTreeData => {
        const nodeToUpdate = findNodeByPath(prevTreeData, parentPath);

        if (
          nodeToUpdate &&
          nodeToUpdate.children &&
          nodeToUpdate.children.length > 0 &&
          childrenApiData.length > 0
        ) {
          return prevTreeData;
        }
        return updateTreeNodeChildren(prevTreeData, parentPath, childrenApiData, shouldSelectFile);
      });
    },
    []
  );

  const findNodeByPath = useCallback(
    (nodes: CustomTreeNodeData[], path: string): CustomTreeNodeData | undefined => {
      for (const node of nodes) {
        if (node.value === path) {
          return node;
        }
        if (node.children) {
          const found = findNodeByPath(node.children, path);
          if (found) return found;
        }
      }
      return undefined;
    },
    []
  );

  const tree = useTree();

  const toggleFileCheck = useCallback((path: string) => {
    setCheckedFiles(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const nodeToSelectedItem = useCallback((node: CustomTreeNodeData): SelectedItem => {
    const nodeName =
      typeof node.label === 'string'
        ? node.label
        : (node.value.split('/').filter(Boolean).pop() ?? '');
    return {
      id: node.id,
      path: node.value,
      name: nodeName,
      isFolder: node.isFolder,
    };
  }, []);

  const isFileSelected = useCallback(() => {
    if (multiple) {
      return checkedFiles.size > 0;
    }
    if (tree.selectedState.length === 0) {
      return false;
    }
    const selectedPath = tree.selectedState[0];
    const selectedNode = findNodeByPath(treeData, selectedPath);

    return selectedNode && !selectedNode.isFolder;
  }, [multiple, checkedFiles.size, tree.selectedState, treeData, findNodeByPath]);

  const handleValidation = useCallback(() => {
    if (multiple) {
      const selectedItems: SelectedItem[] = [];
      checkedFiles.forEach(path => {
        const node = findNodeByPath(treeData, path);
        if (node) {
          selectedItems.push(nodeToSelectedItem(node));
        }
      });
      if (selectedItems.length > 0) {
        (onValidate as (values: SelectedItem[]) => void)(selectedItems);
      }
      return;
    }

    if (tree.selectedState.length === 0) {
      return;
    }

    const selectedPath = tree.selectedState[0];
    const selectedNode = findNodeByPath(treeData, selectedPath);

    if (selectedNode) {
      (onValidate as (value: SelectedItem) => void)(nodeToSelectedItem(selectedNode));
    }
  }, [
    multiple,
    checkedFiles,
    tree.selectedState,
    treeData,
    findNodeByPath,
    onValidate,
    nodeToSelectedItem,
  ]);

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={<div className={styles.title}>{title}</div>}
      withCloseButton={true}
    >
      <div className={styles.container}>
        <p className={styles.subtitle}>{subtitle}</p>
        <Tree
          data={treeData}
          renderNode={payload => {
            const node = payload.node as CustomTreeNodeData;
            return (
              <div className={styles.nodeRow}>
                {multiple && shouldSelectFile && !node.isFolder && (
                  <Checkbox
                    size="xs"
                    checked={checkedFiles.has(node.value)}
                    onChange={() => toggleFileCheck(node.value)}
                    onClick={e => e.stopPropagation()}
                    className={styles.checkbox}
                  />
                )}
                <DirectoryLeaf {...payload} onChildrenLoaded={handleChildrenLoaded} />
              </div>
            );
          }}
          selectOnClick={!multiple}
          tree={tree}
          className={styles.tree}
        />
      </div>

      <div className={styles.buttons}>
        <Button
          disabled={
            shouldSelectFile ? !isFileSelected() : !multiple && tree.selectedState.length === 0
          }
          loading={loading}
          onClick={handleValidation}
        >
          {buttonText}
        </Button>
      </div>
    </Modal>
  );
};

export default DirectorySelectionModal;
